import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/firebase/config";
import { useToast } from "@/components/ui/use-toast";
import { sendAdminNotificationEmail } from "@/lib/emailService";
import {
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
} from "lucide-react";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isHomePage = location.pathname === "/";

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email validation function
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle subscription form submission (subscribes immediately)
  const handleSubscribeClick = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter your email address",
      });
      return;
    }

    if (!isValidEmail(email)) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a valid email address",
      });
      return;
    }

    const normalizedEmail = email.toLowerCase();

    setIsSubmitting(true);
    try {
      // Check if email already exists before subscribing
      const subscribersRef = collection(db, "NewsLetterWeb");
      const q = query(subscribersRef, where("email", "==", normalizedEmail));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        toast({
          variant: "destructive",
          title: "Already Subscribed",
          description: "This email is already subscribed to our newsletter",
        });
        return;
      }

      // Add new subscriber
      await addDoc(collection(db, "NewsLetterWeb"), {
        email: normalizedEmail,
        createdAt: serverTimestamp(),
        source: "website_footer",
        userAgent: navigator.userAgent,
        subscriptionPage: location.pathname,
      });

      // EmailJS configuration
      const serviceId =
        import.meta.env.VITE_EMAILJS_SERVICE_ID || "service_3edbwuj";
      const publicKey =
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "pYRLv9sYDpYiLevTX";

      // Template ID for the admin notification email
      const adminTemplateId =
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "template_y47bxkc";

      // Notify admin only (welcome email to subscriber disabled)
      try {
        await sendAdminNotificationEmail(
          normalizedEmail,
          serviceId,
          adminTemplateId,
          publicKey,
          "website_footer"
        );
        console.log("✅ Admin notification email sent successfully");
      } catch (emailError) {
        console.error(
          "⚠️ Email sending failed, but subscription was saved:",
          emailError
        );
        // Don't fail the subscription if emails fail
      }

      toast({
        title: "Thank You!",
        description:
          "Your subscription has been confirmed. We are glad to have you with us.",
      });

      setEmail("");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to subscribe. Please try again later.",
      });
      console.error("Newsletter subscription error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Smooth scroll function with navigation handling
  const scrollToSection = (sectionId: string, targetPage?: string) => {
    if (targetPage && targetPage !== location.pathname) {
      // Navigate to different page with scroll target
      window.scrollTo(0, 0);
      navigate(targetPage, { state: { scrollTo: sectionId } });
    } else if (!isHomePage) {
      // Scroll to top first
      window.scrollTo(0, 0);
      // Then navigate home with scroll target
      navigate("/", { state: { scrollTo: sectionId } });
    } else {
      // If already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };

  // Quick links data with navigation targets
  const quickLinks = [
    { label: "Home", target: "home" },
    { label: "Sacred Wisdom", target: "sanskrit-quote" },
    { label: "Ancient Practice", target: "yoga-nidra-overview" },
    { label: "Cittasya®", target: "about" },
    { label: "App & Features", target: "features" },
    { label: "Founder", target: "founder", page: "/about" },
  ];

  // Second quick links section with page links
  const pageLinks = [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Blogs", href: "/blogs" },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/privacy" },
  ];

  return (
    <footer className="bg-gradient-to-br from-forest-green to-forest-green/80 text-cloud-white py-16 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cloud-white/10 to-transparent transform skew-y-1"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-soft-saffron/20 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-copper-brown/20 to-transparent rounded-full transform -translate-x-24 translate-y-24"></div>
      </div>

      {/* Floating meditation elements */}
      <div className="absolute right-10 top-20 hidden lg:block">
        <div className="w-32 h-32 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-b from-soft-saffron/40 to-transparent rounded-full animate-pulse"></div>
          <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full bg-cloud-white/40"></div>
        </div>
      </div>

      <div className="absolute left-10 bottom-32 hidden lg:block">
        <div className="w-24 h-24 opacity-20">
          <div
            className="absolute inset-0 bg-gradient-to-t from-copper-brown/40 to-transparent rounded-full animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <img
                  src="/citt-white.svg"
                  alt="Cittasya® Logo"
                  className="h-14 w-auto filter sepia-1 saturate-150 hue-rotate-15 brightness-110"
                />
              </div>
              <p className="text-cloud-white/80 leading-relaxed max-w-md mb-6">
                Cittasya® is a wellness brand dedicated to the healing power of
                conscious relaxation through the art and science of Yoga Nidra.
              </p>

              {/* Newsletter Signup */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Stay Connected</h4>
                <form
                  onSubmit={handleSubscribeClick}
                  className="flex flex-col sm:flex-row gap-3 max-w-md"
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="flex-1 px-4 py-2 rounded-lg bg-white/90 border border-cloud-white/30 text-forest-green placeholder:text-gray-500 focus:outline-none focus:border-soft-saffron focus:bg-white focus:ring-2 focus:ring-soft-saffron/20 transition-all duration-200"
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-cloud-white hover:bg-soft-saffron text-forest-green hover:text-cloud-white px-6 py-2 rounded-lg font-medium min-w-[120px]"
                  >
                    {isSubmitting ? (
                      <span className="animate-spin">🔆</span>
                    ) : (
                      "Subscribe"
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Quick Links 1 - Navigation */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.target, link.page)}
                      className="text-cloud-white/80 hover:text-soft-saffron transition-all duration-200 hover:translate-x-1 transform"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links 2 - Pages */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Pages</h4>
              <ul className="space-y-3">
                {pageLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      onClick={() => window.scrollTo(0, 0)}
                      className="text-cloud-white/80 hover:text-soft-saffron transition-all duration-200 hover:translate-x-1 transform inline-block"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* App & Social */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Download App</h4>
              <div className="space-y-3 mb-6">
                {/* App Store Button */}
                <button className="group w-full overflow-hidden rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                  <div className="bg-black text-white px-4 py-2 flex items-center gap-3 rounded-lg border border-gray-600 hover:border-gray-400 transition-all duration-300">
                    <img
                      src="/apple.svg"
                      alt="Apple Logo"
                      className="w-8 h-8"
                    />
                    <div className="text-left">
                      <div className="text-xs text-gray-300">
                        Download on the
                      </div>
                      <div className="text-sm font-semibold leading-tight">
                        App Store
                      </div>
                    </div>
                  </div>
                </button>

                {/* Google Play Button */}
                <button className="group w-full overflow-hidden rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                  <div className="bg-black text-white px-4 py-2 flex items-center gap-3 rounded-lg border border-gray-600 hover:border-gray-400 transition-all duration-300">
                    <img
                      src="/google-play.svg"
                      alt="Google Play Logo"
                      className="w-8 h-8"
                    />
                    <div className="text-left">
                      <div className="text-xs text-gray-300">GET IT ON</div>
                      <div className="text-sm font-semibold leading-tight">
                        Google Play
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Social Icons */}
              <div>
                <h4 className="font-semibold text-lg mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  {[
                    {
                      icon: Instagram,
                      name: "Instagram",
                      href: "https://instagram.com/cittasyaMind",
                    },
                    {
                      icon: Youtube,
                      name: "YouTube",
                      href: "https://youtube.com/@cittasyaMind",
                    },
                    {
                      icon: Linkedin,
                      name: "LinkedIn",
                      href: "https://linkedin.com/company/cittasyaMind",
                    },
                    {
                      icon: Twitter,
                      name: "Twitter",
                      href: "https://x.com/cittasyaMind",
                    },
                  ].map((social) => {
                    const IconComponent = social.icon;
                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        className="w-10 h-10 rounded-full bg-cloud-white/10 flex items-center justify-center hover:bg-soft-saffron hover:text-forest-green transition-all duration-300 group"
                        title={social.name}
                      >
                        <IconComponent className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-cloud-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-cloud-white/60 text-sm text-center md:text-left">
                <div className="mb-2">
                  © {new Date().getFullYear()} Cittasya®. All rights reserved.
                </div>
              </div>

              <div className="flex space-x-6 text-sm">
                <Link
                  to="/privacy"
                  onClick={() => {
                    window.scrollTo(0, 0);
                  }}
                  className="text-cloud-white/60 hover:text-soft-saffron transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  onClick={() => {
                    window.scrollTo(0, 0);
                  }}
                  className="text-cloud-white/60 hover:text-soft-saffron transition-colors"
                >
                  Terms of Use
                </Link>
                <Link
                  to="/contact"
                  onClick={() => {
                    window.scrollTo(0, 0);
                  }}
                  className="text-cloud-white/60 hover:text-soft-saffron transition-colors"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
